import * as React from "react";
import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { RouteHelper } from "../IntranetRouter";
import { AnomaliaBurlington, BuscarAnomaliasBurlingtonResponse, EnviarAnomaliasBurlingtonPorCorreoResponse, LimpiarCuentasBurlingtonResponse } from "../models/dataModels";
import { I18n } from "../utils/I18n";
import { logAndExtractInfoFromException } from "../utils/ValuesFormatter";
import { LicenciaContenidosEditTabs } from "./Edit";
import { LicenciasColegioService } from "./LicenciasColegioService";

export interface AnomaliasBurlingtonModalProps {
    /** Curso escolar cuyas fechas acotan las trazas. */
    cursoEscolarId: number;
    /** Nombre del curso escolar, para el texto del modal. */
    cursoEscolar: string;
    onClose: () => void;
}

const fechaHora = (valor: Date | string | null | undefined) =>
    valor == null ? '' : I18n.Strings.formatDateTime(new Date(valor));

const fecha = (valor: Date | string | null | undefined) =>
    valor == null ? '' : I18n.Strings.formatDate(new Date(valor));

/** Enlace a la pestaña Burlington de la licencia, en una pestaña nueva para revisar varias seguidas. */
const enlaceLicencia = (item: AnomaliaBurlington) => item.licenciaId == null
    ? <span className="text-muted">Sin licencia</span>
    : <a href={RouteHelper.editLicenciasColegio(item.licenciaId, item.usuarioRelacionadoId, null, LicenciaContenidosEditTabs.Burlington)}
        target="_blank" rel="noopener noreferrer">
        {item.licenciaId} <i className="bi bi-box-arrow-up-right" />
    </a>;

/** Enlace a la ficha del usuario relacionado, en una pestaña nueva. */
const enlaceUsuario = (item: AnomaliaBurlington) => item.usuarioRelacionadoId == null
    ? <span className="text-muted">Sin usuario</span>
    : <a href={RouteHelper.editUsuarioColegio(item.usuarioRelacionadoId)} target="_blank" rel="noopener noreferrer">
        {item.usuario || item.usuarioRelacionadoId} <i className="bi bi-box-arrow-up-right" />
    </a>;

/** Pasos de la limpieza: enviar el Excel por correo, confirmarlo y borrar en Burlington. */
type FaseLimpieza = 'inicial' | 'enviando' | 'enviado' | 'limpiando' | 'limpiado';

/** Lista de emails descartados en la limpieza, con su motivo. */
const descartados = (titulo: string, emails: string[] | null) => (emails ?? []).length === 0 ? null
    : <p className="small mb-1"><b>{titulo} ({emails.length}):</b> {emails.join(", ")}</p>;

/** Resultado de la limpieza: cada cuenta borrada (o el error) y los emails descartados. */
const resultadoLimpieza = (limpieza: LimpiarCuentasBurlingtonResponse) => {
    const borrados = limpieza.borrados ?? [];
    const correctos = borrados.filter(p => p.isOk).length;

    return <div className="mb-3">
        <div className={`alert ${correctos === borrados.length ? 'alert-success' : 'alert-danger'}`}>
            Limpieza terminada: <b>{correctos}</b> de <b>{borrados.length}</b> cuentas borradas en Burlington.
        </div>
        {borrados.length > 0 && <div className="table-responsive" style={{ maxHeight: 240 }}>
            <table className="table table-sm table-striped">
                <thead>
                    <tr>
                        <th>Email Burlington</th>
                        <th>Resultado</th>
                        <th>Respuesta de Burlington</th>
                    </tr>
                </thead>
                <tbody>
                    {borrados.map(p => <tr key={p.email} className={p.isOk ? '' : 'text-danger'}>
                        <td>{p.email}</td>
                        <td>{p.isOk ? 'Borrada' : 'Error'}</td>
                        <td className="small">{p.respuesta}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>}
        {descartados("No se han borrado por ser el email de un usuario del colegio", limpieza.excluidosUsuarioColegio)}
        {descartados("No se han borrado porque ya estaban borradas", limpieza.excluidosYaBorrados)}
    </div>;
};

/**
 * Anomalías de las trazas de sincronización con Burlington del colegio en el curso escolar: las huérfanas
 * (su usuario ya no existe, así que no salen en ninguna licencia) y las hechas con un email distinto del
 * login actual de su usuario. Cada una enlaza con la licencia en la que comprobarla. "Realizar limpieza" manda
 * el Excel por correo al usuario actual y, cuando confirma que lo ha recibido, borra en Burlington las cuentas
 * de esos emails que no son el login de nadie del colegio.
 */
export function AnomaliasBurlingtonModal({ cursoEscolarId, cursoEscolar, onClose }: AnomaliasBurlingtonModalProps) {
    const [resultado, setResultado] = useState<BuscarAnomaliasBurlingtonResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [exportando, setExportando] = useState(false);
    const [faseLimpieza, setFaseLimpieza] = useState<FaseLimpieza>('inicial');
    const [envio, setEnvio] = useState<EnviarAnomaliasBurlingtonPorCorreoResponse | null>(null);
    const [limpieza, setLimpieza] = useState<LimpiarCuentasBurlingtonResponse | null>(null);

    const cargar = () => {
        setResultado(null);
        new LicenciasColegioService().anomaliasBurlington(cursoEscolarId)
            .then(setResultado)
            .catch(err => setError(logAndExtractInfoFromException(err)));
    };

    useEffect(cargar, [cursoEscolarId]);

    const exportar = () => {
        setExportando(true);
        new LicenciasColegioService().exportarAnomaliasBurlington(cursoEscolarId)
            .catch(err => setError(logAndExtractInfoFromException(err)))
            .finally(() => setExportando(false));
    };

    /** Primer paso: el Excel por correo al usuario actual. */
    const enviarCorreo = () => {
        setError(null);
        setFaseLimpieza('enviando');
        new LicenciasColegioService().enviarAnomaliasBurlingtonPorCorreo(cursoEscolarId)
            .then(resp => {
                setEnvio(resp);
                setFaseLimpieza('enviado');
            })
            .catch(err => {
                setError(logAndExtractInfoFromException(err));
                setFaseLimpieza('inicial');
            });
    };

    /** Segundo paso: borrar en Burlington las cuentas de las trazas del Excel enviado. */
    const limpiar = () => {
        setError(null);
        setFaseLimpieza('limpiando');
        new LicenciasColegioService().limpiarCuentasBurlington(cursoEscolarId, envio?.logIds ?? [])
            .then(resp => {
                setLimpieza(resp);
                setFaseLimpieza('limpiado');
                cargar();
            })
            .catch(err => {
                setError(logAndExtractInfoFromException(err));
                setFaseLimpieza('enviado');
            });
    };

    const ocupado = faseLimpieza === 'enviando' || faseLimpieza === 'limpiando';
    const hayAnomalias = resultado != null && (resultado.totalHuerfanas + resultado.totalEmailDistinto) > 0;
    const cerrar = () => { if (!ocupado) { onClose(); } };

    const tabla = (titulo: string, ayuda: string, filas: AnomaliaBurlington[] | null, total: number) => {
        const items = filas ?? [];

        return <>
            <h5 className="mt-3">{titulo} ({total})</h5>
            <p className="text-muted small mb-2">
                {ayuda}
                {items.length < total && <> Se muestran las primeras <b>{items.length}</b> de <b>{total}</b>.</>}
            </p>
            {items.length === 0
                ? <p className="mb-0">No hay ninguna.</p>
                : <div className="table-responsive" style={{ maxHeight: 320 }}>
                    <table className="table table-sm table-striped">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Acción</th>
                                <th>Email Burlington</th>
                                <th>Email Hub</th>
                                <th>Usuario</th>
                                <th>Credencial</th>
                                <th>ISBN</th>
                                <th>Licencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => <tr key={item.logId}>
                                <td>{fechaHora(item.fecha)}</td>
                                <td>{I18n.Strings.getEnumText(`TipoAccionBurlington.${item.tipoAccionBurlington}`)}</td>
                                <td>{item.email}</td>
                                <td>{item.loginUsuario}</td>
                                <td>{enlaceUsuario(item)}</td>
                                <td>{item.credencial}</td>
                                <td>{item.isbn}</td>
                                <td>{enlaceLicencia(item)}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>}
        </>;
    };

    // Ocho columnas con emails largos: el modal ocupa el 90 % del ancho de la pantalla.
    return <Modal isOpen={true} style={{ width: '90vw', maxWidth: '90vw' }} toggle={cerrar}>
        <ModalHeader close={<button className="btn-close" disabled={ocupado} onClick={cerrar} />}>
            Anomalías de Burlington
        </ModalHeader>
        <ModalBody>
            {error && <div className="alert alert-danger">{error}</div>}

            {faseLimpieza === 'enviado' && envio && <div className="alert alert-warning">
                Se ha enviado el Excel con <b>{envio.logIds?.length ?? 0}</b> trazas a <b>{envio.email}</b>. Compruebe que
                lo ha recibido antes de confirmar: se borrarán en Burlington las cuentas de los emails con los que se
                sincronizaron, salvo los que son el login de un usuario del colegio y los ya borrados.
            </div>}

            {faseLimpieza === 'limpiado' && limpieza && resultadoLimpieza(limpieza)}

            {!error && resultado == null && <div className="text-center text-muted py-3">
                <span className="spinner-border spinner-border-sm me-1" role="status" /> Buscando anomalías…
            </div>}

            {resultado && <>
                <p className="mb-0">
                    Trazas de sincronización con Burlington de <b>{cursoEscolar}</b>: del {fecha(resultado.desde)}
                    {resultado.hasta != null ? <> al {fecha(resultado.hasta)}</> : <> en adelante</>} (desde el
                    inicio de la campaña hasta el fin del curso).
                </p>

                {tabla("Trazas huérfanas",
                    "Su usuario ya no existe (se borró o se unió a otro antes de que la unión las traspasara), así " +
                    "que no salen en la pestaña Burlington de ninguna licencia. La licencia enlazada es la que tiene " +
                    "la misma credencial o la de quien hoy tiene ese email como login; el usuario y su email son " +
                    "los de esa licencia.",
                    resultado.huerfanas, resultado.totalHuerfanas)}

                {tabla("Email distinto del login del usuario",
                    "La traza es de un usuario que existe, pero con Burlington se usó un email que no es su login " +
                    "actual: su cuenta de Burlington puede estar con el email antiguo. Los borrados \"con email\" " +
                    "lanzados desde una licencia también salen aquí. La licencia enlazada es una de Burlington del " +
                    "usuario, preferentemente de este curso.",
                    resultado.emailDistinto, resultado.totalEmailDistinto)}
            </>}
        </ModalBody>
        <ModalFooter>
            {faseLimpieza === 'inicial' && <Button color="warning" className="rounded-pill" disabled={!hayAnomalias} onClick={enviarCorreo}
                title="Manda el Excel de anomalías a su email; después pedirá confirmación para borrar las cuentas en Burlington">
                Realizar limpieza
            </Button>}
            {faseLimpieza === 'enviando' && <Button color="warning" className="rounded-pill" disabled>
                <span className="spinner-border spinner-border-sm me-1" role="status" /> Enviando el Excel…
            </Button>}
            {faseLimpieza === 'enviado' && <Button color="danger" className="rounded-pill" onClick={limpiar}>
                Confirmo que he recibido el mail, eliminar usuarios en burlington
            </Button>}
            {faseLimpieza === 'limpiando' && <Button color="danger" className="rounded-pill" disabled>
                <span className="spinner-border spinner-border-sm me-1" role="status" /> Eliminando usuarios en Burlington…
            </Button>}

            <Button color="secondary" disabled={resultado == null || exportando} onClick={exportar}
                title="Email Hub, email Burlington, tipo del problema, ISBN y credencial de todas las anomalías">
                {exportando
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" /> Exportando…</>
                    : <><i className="bi bi-file-earmark-excel me-1" />Exportar a Excel</>}
            </Button>
            <Button color="primary" disabled={ocupado} onClick={cerrar}>Cerrar</Button>
        </ModalFooter>
    </Modal>;
}
